import { useState, useEffect, useCallback } from 'react';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

export interface Ingredient {
  id: number;
  name: string;
  category: string;
  category_display: string;
  unit: string;
  unit_display: string;
  cost_per_unit: string | null;
  netto_weight_g: string | null;
  gross_weight_g: string | null;
  supplier: string;
  producer: string;
  notes: string;
  calories: string | null;
  protein_g: string | null;
  carbs_g: string | null;
  fat_g: string | null;
  fiber_g: string | null;
  sugar_g: string | null;
  created_at: string;
  updated_at: string;
}

export const CATEGORY_OPTIONS = [
  { value: 'flour', label: 'Flour' },
  { value: 'sugar', label: 'Sugar & Sweeteners' },
  { value: 'fat', label: 'Fats & Oils' },
  { value: 'dairy', label: 'Dairy' },
  { value: 'egg', label: 'Eggs' },
  { value: 'fruit', label: 'Fruits' },
  { value: 'nut', label: 'Nuts & Seeds' },
  { value: 'spice', label: 'Spices & Flavourings' },
  { value: 'leavening', label: 'Leavening Agents' },
  { value: 'salt', label: 'Salt' },
  { value: 'liquid', label: 'Liquids' },
  { value: 'chocolate', label: 'Chocolate & Cocoa' },
  { value: 'other', label: 'Other' },
];

export const UNIT_OPTIONS = [
  { value: 'kg', label: 'kg' },
  { value: 'g', label: 'g' },
  { value: 'lbs', label: 'lbs' },
  { value: 'oz', label: 'oz' },
  { value: 'l', label: 'l' },
  { value: 'ml', label: 'ml' },
  { value: 'gallon', label: 'gallon' },
  { value: 'tsp', label: 'tsp' },
  { value: 'tbsp', label: 'tbsp' },
  { value: 'cup', label: 'cup' },
  { value: 'piece', label: 'piece' },
  { value: 'box', label: 'box' },
  { value: 'bag', label: 'bag' },
];

const emptyForm = {
  name: '',
  category: 'other',
  unit: 'kg',
  cost_per_unit: '',
  netto_weight_g: '',
  gross_weight_g: '',
  supplier: '',
  producer: '',
  notes: '',
  calories: '',
  protein_g: '',
  carbs_g: '',
  fat_g: '',
  fiber_g: '',
  sugar_g: '',
  critical_amount: '',
};

export type IngredientForm = typeof emptyForm;

export function useCompanyIngredients() {
  const { isExpired } = useAuth();

  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const [showModal, setShowModal] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<Ingredient | null>(null);
  const [form, setForm] = useState<IngredientForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ error: '', success: '' });

  const [deleteState, setDeleteState] = useState<{
    showModal: boolean; ingredient: Ingredient | null; deleting: boolean;
  }>({ showModal: false, ingredient: null, deleting: false });

  const fetchIngredients = useCallback(async () => {
    try {
      const res = await companyAPI.getIngredients();
      setIngredients(res.data);
    } catch (err) {
      console.error('Failed to fetch ingredients:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchIngredients(); }, [fetchIngredients]);

  const filtered = ingredients.filter((i) => {
    const matchSearch = i.name.toLowerCase().includes(search.toLowerCase()) ||
      i.supplier.toLowerCase().includes(search.toLowerCase());
    const matchCategory = categoryFilter === 'all' || i.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  const handleOpenCreate = () => {
    setEditingIngredient(null);
    setForm(emptyForm);
    setFeedback({ error: '', success: '' });
    setShowModal(true);
  };

  const handleOpenEdit = (ingredient: Ingredient) => {
    setEditingIngredient(ingredient);
    setForm({
      name: ingredient.name,
      category: ingredient.category,
      unit: ingredient.unit,
      cost_per_unit: ingredient.cost_per_unit ?? '',
      netto_weight_g: ingredient.netto_weight_g ?? '',
      gross_weight_g: ingredient.gross_weight_g ?? '',
      supplier: ingredient.supplier,
      producer: ingredient.producer,
      notes: ingredient.notes,
      calories: ingredient.calories ?? '',
      protein_g: ingredient.protein_g ?? '',
      carbs_g: ingredient.carbs_g ?? '',
      fat_g: ingredient.fat_g ?? '',
      fiber_g: ingredient.fiber_g ?? '',
      sugar_g: ingredient.sugar_g ?? '',
      critical_amount: (ingredient as any).critical_amount ?? '',
    });
    setFeedback({ error: '', success: '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFeedback({ error: 'Name is required.', success: '' });
      return;
    }
    setSaving(true);
    setFeedback({ error: '', success: '' });
    const payload: any = {
      name: form.name.trim(),
      category: form.category,
      unit: form.unit,
      supplier: form.supplier,
      producer: form.producer,
      notes: form.notes,
    };
    // Only include numeric fields if non-empty
    const numericFields = ['cost_per_unit', 'netto_weight_g', 'gross_weight_g', 'calories', 'protein_g', 'carbs_g', 'fat_g', 'fiber_g', 'sugar_g', 'critical_amount'] as const;
    numericFields.forEach((f) => {
      payload[f] = form[f] !== '' ? form[f] : null;
    });
    try {
      if (editingIngredient) {
        const res = await companyAPI.updateIngredient(editingIngredient.id, payload);
        setIngredients(prev => prev.map(i => i.id === editingIngredient.id ? res.data : i));
      } else {
        const res = await companyAPI.createIngredient(payload);
        setIngredients(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setShowModal(false);
      setFeedback({ error: '', success: editingIngredient ? 'Ingredient updated.' : 'Ingredient added.' });
    } catch (err: any) {
      const msg = err?.response?.data?.name?.[0] || err?.response?.data?.detail || 'Failed to save ingredient.';
      setFeedback({ error: msg, success: '' });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = (ingredient: Ingredient) => {
    setDeleteState({ showModal: true, ingredient, deleting: false });
  };

  const handleDelete = async () => {
    if (!deleteState.ingredient) return;
    setDeleteState(prev => ({ ...prev, deleting: true }));
    try {
      await companyAPI.deleteIngredient(deleteState.ingredient.id);
      setIngredients(prev => prev.filter(i => i.id !== deleteState.ingredient!.id));
      setDeleteState({ showModal: false, ingredient: null, deleting: false });
      setFeedback({ error: '', success: 'Ingredient deleted.' });
    } catch {
      setDeleteState(prev => ({ ...prev, deleting: false }));
      setFeedback({ error: 'Failed to delete ingredient.', success: '' });
    }
  };

  const closeModal = () => setShowModal(false);
  const closeDeleteModal = () => setDeleteState({ showModal: false, ingredient: null, deleting: false });

  return {
    isExpired,
    ingredients,
    loading,
    search,
    setSearch,
    categoryFilter,
    setCategoryFilter,
    filtered,
    showModal,
    editingIngredient,
    form,
    setForm,
    saving,
    feedback,
    setFeedback,
    deleteState,
    handleOpenCreate,
    handleOpenEdit,
    handleSave,
    handleConfirmDelete,
    handleDelete,
    closeModal,
    closeDeleteModal,
  };
}
